<script lang="ts">
  import { router } from "@inertiajs/svelte";
  import TripSubNav from "../../components/navigation/TripSubNav.svelte";
  import Button from "../../components/input/Button.svelte";
  import InlineSpinner from "../../components/feedback/InlineSpinner.svelte";
  import ConfirmDialog from "../../components/overlay/ConfirmDialog.svelte";
  import EmptyState from "../../components/feedback/EmptyState.svelte";

  type Album = {
    id: string;
    title?: string;
    album_date?: string;
    cover_photo_url?: string;
    photos_count?: number;
  };

  type Photo = {
    id: string;
    image_url: string;
    thumbnail_url?: string;
    caption?: string;
    taken_date?: string;
    place_name?: string;
  };

  let { trip, albums = [], allPhotos = [] }: {
    trip: any;
    albums?: Album[];
    allPhotos?: Photo[];
  } = $props();

  let localAlbums = $state<Album[]>([]);
  let albumsHydrated = false;
  let selectedAlbum = $state<Album | null>(null);
  let albumPhotos = $state<Photo[]>([]);
  let loading = $state(false);
  let showCreateModal = $state(false);
  let showUploadModal = $state(false);
  let submitting = $state(false);
  let lightboxPhoto = $state<Photo | null>(null);
  let deleteAlbumId = $state<string | null>(null);
  let confirmDelete = $state(false);
  let viewMode = $state<"albums" | "all">("albums");
  let dateFilter = $state("");

  let createForm = $state({ title: "", album_date: "" });
  let uploadForm = $state({ caption: "", place_name: "", taken_date: "", imageFile: null as File | null });

  function csrfToken(): string {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
  }

  $effect(() => {
    if (albumsHydrated) return;
    localAlbums = [...albums];
    albumsHydrated = true;
  });

  async function openAlbum(album: Album) {
    loading = true;
    selectedAlbum = album;
    const res = await fetch(`/api/v1/trips/${trip.id}/trip_photos?album_id=${album.id}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
    });
    const json = await res.json();
    albumPhotos = json.data || json || [];
    loading = false;
  }

  function closeAlbum() {
    selectedAlbum = null;
    albumPhotos = [];
  }

  async function createAlbum() {
    if (!createForm.title.trim() && !createForm.album_date) return;
    submitting = true;
    const res = await fetch(`/api/v1/trips/${trip.id}/trip_albums`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
      body: JSON.stringify({ trip_album: createForm }),
    });
    submitting = false;
    if (!res.ok) return;
    const json = await res.json();
    const album = json.data || json;
    localAlbums = [...localAlbums, album];
    showCreateModal = false;
    createForm = { title: "", album_date: "" };
  }

  async function deleteAlbum() {
    if (!deleteAlbumId) return;
    await fetch(`/api/v1/trips/${trip.id}/trip_albums/${deleteAlbumId}`, {
      method: "DELETE",
      headers: { "X-CSRF-Token": csrfToken() },
    });
    localAlbums = localAlbums.filter((a) => a.id !== deleteAlbumId);
    deleteAlbumId = null;
  }

  async function uploadPhoto() {
    if (!uploadForm.imageFile) return;
    submitting = true;
    const fd = new FormData();
    fd.append("trip_photo[image]", uploadForm.imageFile);
    if (uploadForm.caption) fd.append("trip_photo[caption]", uploadForm.caption);
    if (uploadForm.place_name) fd.append("trip_photo[place_name]", uploadForm.place_name);
    if (uploadForm.taken_date) fd.append("trip_photo[taken_date]", uploadForm.taken_date);
    if (selectedAlbum) fd.append("trip_photo[album_id]", selectedAlbum.id);

    const res = await fetch(`/api/v1/trips/${trip.id}/trip_photos`, {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken() },
      body: fd,
    });
    submitting = false;
    if (!res.ok) return;
    const json = await res.json();
    const photo = json.data || json;
    albumPhotos = [...albumPhotos, photo];
    showUploadModal = false;
    uploadForm = { caption: "", place_name: "", taken_date: "", imageFile: null };
  }

  const filteredAllPhotos = $derived(
    dateFilter
      ? allPhotos.filter((p) => p.taken_date === dateFilter)
      : allPhotos
  );
</script>

<TripSubNav {trip} currentPage="album" />

<div class="mx-auto max-w-6xl space-y-6 px-4 py-6 sm:px-6 lg:px-8">
  <!-- Header -->
  <div class="flex items-center justify-between gap-4">
    <div>
      <h1 class="text-2xl font-bold text-primary">여행 앨범</h1>
      <p class="mt-1 text-sm text-muted">날짜별 앨범과 사진을 관리합니다.</p>
    </div>
    <div class="flex gap-2">
      {#if selectedAlbum}
        <Button variant="secondary" size="sm" onclick={() => (showUploadModal = true)}>📸 사진 추가</Button>
        <Button variant="ghost" size="sm" onclick={closeAlbum}>← 목록</Button>
      {:else}
        <Button variant="primary" size="sm" onclick={() => (showCreateModal = true)}>+ 앨범 만들기</Button>
      {/if}
    </div>
  </div>

  <!-- View toggle (all photos vs albums) -->
  {#if !selectedAlbum}
    <div class="flex gap-2">
      {#each [["albums", "📁 앨범"], ["all", "🖼️ 전체 사진"]] as [mode, label]}
        <button
          type="button"
          onclick={() => (viewMode = mode as "albums" | "all")}
          class="rounded-xl px-4 py-2 text-sm font-medium transition-colors
            {viewMode === mode ? 'bg-teal-400/15 text-teal-400 border border-teal-400/30' : 'border border-white/10 text-muted hover:text-secondary'}"
        >
          {label}
        </button>
      {/each}
    </div>
  {/if}

  <!-- Album detail view -->
  {#if selectedAlbum}
    <div class="space-y-4">
      <div class="flex items-center gap-3">
        <h2 class="text-xl font-semibold text-primary">{selectedAlbum.title || selectedAlbum.album_date || "앨범"}</h2>
        <span class="text-sm text-muted">{albumPhotos.length}장</span>
      </div>

      {#if loading}
        <div class="flex items-center justify-center py-16">
          <InlineSpinner size={32} color="#20b2aa" />
        </div>
      {:else if albumPhotos.length === 0}
        <EmptyState icon="📷" title="사진 없음" description="이 앨범에 사진이 없습니다. 사진을 추가해보세요." />
      {:else}
        <div class="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
          {#each albumPhotos as photo}
            <button
              type="button"
              class="group relative overflow-hidden rounded-xl border border-white/8 bg-white/5 aspect-square"
              onclick={() => (lightboxPhoto = photo)}
            >
              <img
                src={photo.thumbnail_url || photo.image_url}
                alt={photo.caption || "photo"}
                class="h-full w-full object-cover transition-transform group-hover:scale-105"
              />
              {#if photo.caption}
                <div class="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 p-2">
                  <p class="text-[11px] text-primary truncate">{photo.caption}</p>
                </div>
              {/if}
            </button>
          {/each}
        </div>
      {/if}
    </div>

  <!-- All photos view -->
  {:else if viewMode === "all"}
    <div class="space-y-4">
      <input
        type="date"
        bind:value={dateFilter}
        class="rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-muted"
      />
      {#if filteredAllPhotos.length === 0}
        <EmptyState icon="📷" title="사진 없음" description="아직 업로드된 사진이 없습니다." />
      {:else}
        <div class="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
          {#each filteredAllPhotos as photo}
            <button
              type="button"
              class="group relative overflow-hidden rounded-xl border border-white/8 aspect-square"
              onclick={() => (lightboxPhoto = photo)}
            >
              <img
                src={photo.thumbnail_url || photo.image_url}
                alt={photo.caption || "photo"}
                class="h-full w-full object-cover transition-transform group-hover:scale-105"
              />
            </button>
          {/each}
        </div>
      {/if}
    </div>

  <!-- Albums grid -->
  {:else}
    {#if localAlbums.length === 0}
      <EmptyState icon="📁" title="앨범 없음" description="아직 앨범이 없습니다. 첫 앨범을 만들어보세요." />
    {:else}
      <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {#each localAlbums as album}
          <div class="group relative overflow-hidden rounded-2xl border border-white/10 bg-slate-900/70">
            <button
              type="button"
              class="block w-full text-left"
              onclick={() => openAlbum(album)}
            >
              <div class="aspect-[4/3] overflow-hidden bg-white/5">
                {#if album.cover_photo_url}
                  <img
                    src={album.cover_photo_url}
                    alt={album.title || "album"}
                    class="h-full w-full object-cover transition-transform group-hover:scale-105"
                  />
                {:else}
                  <div class="flex h-full items-center justify-center text-5xl text-secondary">📷</div>
                {/if}
              </div>
              <div class="p-4">
                <p class="font-semibold text-primary">{album.title || album.album_date || "Untitled"}</p>
                <p class="mt-0.5 text-sm text-muted">{album.photos_count ?? 0}장</p>
              </div>
            </button>
            <button
              type="button"
              aria-label="앨범 삭제"
              class="absolute right-3 top-3 rounded-lg bg-black/50 px-2 py-1 text-xs text-muted opacity-0 group-hover:opacity-100 transition-opacity"
              onclick={(e) => { e.stopPropagation(); deleteAlbumId = album.id; confirmDelete = true; }}
            >
              삭제
            </button>
          </div>
        {/each}
      </div>
    {/if}
  {/if}
</div>

<!-- Lightbox -->
{#if lightboxPhoto}
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
    role="dialog"
    aria-modal="true"
  >
    <button type="button" class="absolute inset-0 w-full h-full" onclick={() => (lightboxPhoto = null)} aria-label="닫기"></button>
    <div class="relative z-10 max-w-3xl w-full">
      <img
        src={lightboxPhoto.image_url}
        alt={lightboxPhoto.caption || "photo"}
        class="max-h-[80vh] w-full rounded-2xl object-contain"
      />
      {#if lightboxPhoto.caption}
        <p class="mt-3 text-center text-sm text-muted">{lightboxPhoto.caption}</p>
      {/if}
    </div>
  </div>
{/if}

<!-- Create Album Modal -->
{#if showCreateModal}
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
    <div class="w-full max-w-md rounded-3xl border border-white/10 bg-slate-950 p-6">
      <div class="mb-5 flex items-center justify-between">
        <h2 class="text-lg font-semibold">새 앨범 만들기</h2>
        <button type="button" class="text-muted hover:text-primary" onclick={() => (showCreateModal = false)}>✕</button>
      </div>
      <div class="space-y-3">
        <input
          bind:value={createForm.title}
          placeholder="앨범 이름"
          class="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-primary placeholder-gray-500 outline-none focus:border-teal-400"
        />
        <div class="flex flex-col gap-1">
          <label for="album-date" class="text-xs text-muted">날짜</label>
          <input
            id="album-date"
            type="date"
            bind:value={createForm.album_date}
            class="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-primary outline-none focus:border-teal-400"
          />
        </div>
        <Button variant="primary" fullWidth loading={submitting} onclick={createAlbum}>만들기</Button>
      </div>
    </div>
  </div>
{/if}

<!-- Upload Photo Modal -->
{#if showUploadModal}
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
    <div class="w-full max-w-md rounded-3xl border border-white/10 bg-slate-950 p-6">
      <div class="mb-5 flex items-center justify-between">
        <h2 class="text-lg font-semibold">사진 추가</h2>
        <button type="button" class="text-muted hover:text-primary" onclick={() => (showUploadModal = false)}>✕</button>
      </div>
      <div class="space-y-3">
        <input
          type="file"
          accept="image/*"
          class="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-muted"
          onchange={(e) => { uploadForm.imageFile = (e.target as HTMLInputElement).files?.[0] ?? null; }}
        />
        <input
          bind:value={uploadForm.caption}
          placeholder="설명 (선택)"
          class="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-primary placeholder-gray-500 outline-none focus:border-teal-400"
        />
        <input
          bind:value={uploadForm.place_name}
          placeholder="장소 이름 (선택)"
          class="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-primary placeholder-gray-500 outline-none focus:border-teal-400"
        />
        <input
          type="date"
          bind:value={uploadForm.taken_date}
          class="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-primary outline-none focus:border-teal-400"
        />
        <Button variant="primary" fullWidth loading={submitting} onclick={uploadPhoto}>업로드</Button>
      </div>
    </div>
  </div>
{/if}

<!-- Delete Confirm -->
<ConfirmDialog
  bind:open={confirmDelete}
  title="앨범 삭제"
  message="이 앨범을 삭제하시겠습니까? 포함된 사진은 삭제되지 않습니다."
  confirmLabel="삭제"
  onconfirm={deleteAlbum}
/>
